import { Select } from 'antd';
import _ from 'lodash';
import moment from 'moment';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getStudentPoints } from '../../../api/students.api';
import useAxios from '../../../hooks/useAxios';
import useScore from '../../../hooks/useScore';
import { IGetStudentPoints } from '../../../models/students.model';
import SelectStudentClass from './SelectStudentClass';
import styled from 'styled-components';
import Loader from '../../Loader';
import TableEmpty from '../../common/TableEmpty';

const PointLoadingWrap = styled.div`
  width: 100%;
  height: calc(100% - 118px);
  position: absolute;
  left: 0;
  top: 55px;
  z-index: 100000;
`;

interface IProps {
  memberCode: string | undefined;
  defaultClassCode?: string;
}

const StudentPoint: FC<IProps> = ({ memberCode, defaultClassCode }) => {
  const { handleData } = useScore();
  const { data: res, isLoading, execute } = useAxios<IGetStudentPoints>();
  const [classCode, setClassCode] = useState<string>();
  const [learningTypeCode, setLearningTypeCode] = useState<string>();
  const [tableHeaders, setTableHeaders] = useState<any>([]);
  const [dates, setDates] = useState<any>([]);
  const [data, setData] = useState<any>([]);
  const { t } = useTranslation();

  useEffect(() => {
    if (defaultClassCode) {
      setClassCode(defaultClassCode);
    }
  }, [defaultClassCode]);

  useEffect(() => {
    if (memberCode && classCode && learningTypeCode) {
      execute(
        getStudentPoints(memberCode, {
          classCode,
          learningTypeCode,
        }),
      );
    }
  }, [memberCode, classCode, learningTypeCode]);

  useEffect(() => {
    if (res && !isLoading) {
      const _data: any = _.cloneDeep(res?.data);
      let _tableHeaders: any = [];
      _data.forEach((item: any) => {
        item.label = `${item.depth1Name}/${item.depth2Name}/${item.subjectName}`;
        _tableHeaders.push(item);
      });
      setData(_data);
      setDates(_.uniqBy(_data, 'planDate'));
      _tableHeaders = _.uniqBy(_tableHeaders, 'label');
      _tableHeaders = _.sortBy(_tableHeaders, 'depth1Name');
      _tableHeaders = handleData(_tableHeaders);
      setTableHeaders(_tableHeaders);
    }
  }, [res, isLoading]);

  const getTotalPoint = (data: any) => {
    let total = 0;

    data.forEach((item: any) => {
      let completeCount = 0;
      let point = 0;
      item.activityList.forEach((subitem: any) => {
        if (subitem.completeYn === 'Y') {
          completeCount++;
          if (subitem.point) {
            point += subitem.point;
          }
        }
      });
      if (completeCount === item.activityList.length) {
        total += point;
      }
    });

    return total;
  };

  const getPoint = (date: string, label: string) => {
    const matchingItems = data.filter(
      (item: any) => item.planDate === date && item.label === label,
    );

    if (matchingItems.length === 0) return 'N/A';

    let totalPoint = 0;
    let inCompileCount = 0;

    matchingItems.forEach((item: any) => {
      const completedActivities = item.activityList.filter((subitem: any) => subitem.completeYn === 'Y');
      const itemPoint = completedActivities.reduce((sum: number, subitem: any) => sum + (subitem.point || 0), 0);
      
      if (completedActivities.length === item.activityList.length) {
        totalPoint += itemPoint;
      } else {
        inCompileCount++;
      }
    });

    return inCompileCount === matchingItems.length ? 'X' : totalPoint;
  };

  return (
    <>
      {isLoading && (
        <PointLoadingWrap>
          <Loader isInBox={true} />
        </PointLoadingWrap>
      )}
      <div className="comm-bx__between">
        <div className="comm-sch_result">Total Point: <b>{(res)?getTotalPoint(res?.data):0} </b></div>
        <div className="comm-btn__area right">
          <div style={{ width: '200px' }}>
            <SelectStudentClass
              memberCode={memberCode}
              classCode={classCode}
              setClassCode={setClassCode}
            />
          </div>
          <div className="w-150p">
            <Select
              className="w-100"
              placeholder={t('common.column.category')}
              value={learningTypeCode}
              onChange={(value) => setLearningTypeCode(value)}
            >
              <Select.Option value="PP">iCoaching</Select.Option>
              <Select.Option value="RV">e-Learning</Select.Option>
            </Select>
          </div>
        </div>
      </div>
      {res?.data && (
        <>
          {tableHeaders.length ? (
            <div className='comm-table__area rowType stats mt-0'>
              <table
                style={{
                  //maxWidth: `${((tableHeaders?.length || 0) + 1) * 200}px`,
                }}
              >
                <colgroup>
                  {[...Array((tableHeaders?.length || 0) + 1)].map(
                    (item, index) => (
                      <col key={index} width="200px" />
                    ),
                  )}
                </colgroup>
                <tbody>
                  <tr>
                    <th rowSpan={3} />
                    {tableHeaders?.map((item: any, index: number) => {
                      if (item.colSpan !== 0)
                        return (
                          <th key={index} colSpan={item.colSpan}>
                            {item.depth1Name}
                          </th>
                        );
                      else return null;
                    })}
                  </tr>
                  <tr>
                    {tableHeaders?.map((item: any) => (
                      <th>{item.depth2Name}</th>
                    ))}
                  </tr>
                  <tr>
                    {tableHeaders?.map((item: any) => (
                      <th>{item.subjectName}</th>
                    ))}
                  </tr>
                  {dates.map((item: any, index: number) => (
                    <tr key={index}>
                      <td>{moment(item.planDate).format('M/D')}</td>
                      {tableHeaders?.map((subitem: any, subindex: number) => (
                        <td
                          key={subindex}
                          style={{
                            backgroundColor:
                              getPoint(item.planDate, subitem.label) === 'N/A'
                                ? 'gainsboro'
                                : '',
                          }}
                        >
                          {getPoint(item.planDate, subitem.label) === 'N/A'
                            ? ''
                            : getPoint(item.planDate, subitem.label)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <div className='comm-table__area mt-0'><TableEmpty text={t('shuttle.text.40')} /></div>}
        </>
      )}
    </>
  );
};

export default StudentPoint;
